import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import DateRange from '@/components/date/DateRange';
import dayjs from '@/config/dayjs';
import { dashboardMobileWidthValue } from '@/constants';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import {
  DataRangeFilterEnum,
  getDataRangeFilterLabel,
  getDateRangeFromFilter,
} from '@/constants/dates';
import { useLocalization } from '@/hooks/useLocalization';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import type { StandardWidgetProps } from '@/models/WidgetModels';
import AssetAllocationChart from '@/modules/portfolio/charts/assetAllocationChart';
import { parseAppDate } from '@/utils/date';
import { ContentContainer } from '@/widgets/contentContainer';

/** A dashboard tile is much shorter than the full Asset Allocation page. */
const WIDGET_CHART_HEIGHT = 360;

/** The ticket's presets, in the shared picker's order. */
const PRESETS: ReadonlySet<DataRangeFilterEnum> = new Set([
  DataRangeFilterEnum.Trailing7Days,
  DataRangeFilterEnum.Trailing30Days,
  DataRangeFilterEnum.Trailing90Days,
  DataRangeFilterEnum.Trailing1Month,
  DataRangeFilterEnum.Trailing3Months,
  DataRangeFilterEnum.Trailing6Months,
  DataRangeFilterEnum.Trailing9Months,
  DataRangeFilterEnum.Trailing1Year,
]);

/** The range the widget opens on. */
const DEFAULT_PRESET = DataRangeFilterEnum.Trailing1Year;

/**
 * No exclusions. The chart falls back to the filters saved from a client's
 * Asset Allocation page when given none, and those would drop that client's
 * accounts from the whole book.
 */
const NO_EXCLUSIONS: number[] = [];

/** Whether the picker offers a preset. */
function isPreset(filter: DataRangeFilterEnum): boolean {
  return PRESETS.has(filter);
}

/**
 * The advisor's whole book as a stacked area chart by asset class over a date
 * range (legacy `advisor-pd-historical`, IU-328). The chart is asked for no
 * client, so the advisor endpoint aggregates every client the advisor can see.
 * It opens on Last Year, as legacy opened on its last twelve months, with that
 * preset shown as selected: the picker only marks a preset picked while it is
 * open, so the widget tracks the label itself. It ignores the filters saved
 * from a client's Asset Allocation page, which would leave accounts out of the
 * book. Clearing
 * the range blanks the body until a new one is picked, and a failed load
 * offers Retry, as the other advisor widgets do. A short range keeps its
 * picked dates on the axis even when it holds a single point, and the value
 * axis is kept narrow so the chart fills a half-width or phone tile.
 */
export default function DashboardHistoricalDiversificationWidget({
  title,
  onDataLoaded,
  container = 'widget',
}: StandardWidgetProps) {
  const { t } = useLocalization();
  const widgetTitle =
    title ||
    t(
      'widgetHistoricalPortfolioDiversification',
      'Historical Portfolio Diversification',
    );

  const [range, setRange] = useState(() =>
    getDateRangeFromFilter(DEFAULT_PRESET),
  );
  const [shortcutLabel, setShortcutLabel] = useState(() =>
    getDataRangeFilterLabel(DEFAULT_PRESET),
  );
  const { ref, widthPx, isBelow } = useWidgetWidthIsBelow(
    true,
    dashboardMobileWidthValue,
  );

  const chartStartDate = parseAppDate(range.startDate)?.toDate();
  const chartEndDate = parseAppDate(range.endDate)?.toDate();

  return (
    <ContentContainer
      container={container}
      ref={ref}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2,
        }}
      >
        <Typography variant='h5'>{widgetTitle}</Typography>
        <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
          <DateRange
            startDate={range.startDate}
            endDate={range.endDate}
            onChange={(startDate, endDate, label) => {
              setRange({ startDate, endDate });
              setShortcutLabel(label ?? '');
            }}
            selectedShortcutLabel={shortcutLabel}
            shortcutsFilter={isPreset}
            maxDate={dayjs()}
            useWidgetNarrowIconField={isBelow}
          />
        </Box>
      </Box>
      {chartStartDate && chartEndDate && (
        <AssetAllocationChart
          startDate={chartStartDate}
          endDate={chartEndDate}
          displayMode='dollar'
          excludedAccounts={NO_EXCLUSIONS}
          excludedCategories={NO_EXCLUSIONS}
          excludedAccountTypes={NO_EXCLUSIONS}
          excludedInstitutions={NO_EXCLUSIONS}
          onDataLoaded={onDataLoaded}
          containerWidth={widthPx ?? undefined}
          height={WIDGET_CHART_HEIGHT}
          showRetry
          axisSpansRange
          compactAxes
        />
      )}
    </ContentContainer>
  );
}
